<template>
  <a-drawer
    :title="`Repayment Ledger`"
    :width="900"
    :visible="visible"
    @close="$emit('close')"
    :closable="true"
  >
    <div v-if="repaymentData">
      <!-- Invoice Header -->
      <div class="invoice-header">
        <div class="invoice-title">
          <span class="invoice-number"
            >Invoice #{{
              repaymentData.invoiceIdFormatted || repaymentData.invoiceId
            }}</span
          >
          <span class="buyer-name"
            >– {{ repaymentData.buyerName || "N/A" }}</span
          >
        </div>
        <div class="status-badge">
          <StatusTag :status="repaymentData.repaymentStatus" />
        </div>
      </div>

      <!-- Repayment Summary Section -->
      <div class="summary-section">
        <h3 class="section-title">Repayment Summary</h3>
        <div class="summary-grid">
          <div class="summary-card">
            <div class="card-label">Total Value</div>
            <div class="card-value primary">
              {{ formatAmount(repaymentData.totalValue) }}
            </div>
          </div>
          <div class="summary-card">
            <div class="card-label">Dispensed So Far</div>
            <div class="card-value secondary">
              {{ formatAmount(repaymentData.dispensedSoFar) }}
            </div>
          </div>
          <div class="summary-card">
            <div class="card-label">Repaid So Far</div>
            <div class="card-value success">
              {{ formatAmount(repaymentData.repaid) }}
            </div>
          </div>
          <div class="summary-card">
            <div class="card-label">Remaining Balance</div>
            <div class="card-value danger">
              {{ formatAmount(repaymentData.outstanding) }}
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons"
      v-if="repaymentData.repaymentStatus !== 'Repaid'">
        <a-button
          type="primary"
          icon="plus"
          style="
            background: #26a246;
            border-color: #26a246;
            border-radius: 6px;
            margin-right: 12px;
          "
          @click="recordNewRepayment"
        >
          Record New Repayment
        </a-button>
                <a-button 
          icon="bell"
          style="border-radius: 6px"
          @click="sendFollowUp"
        >
          Send Follow-Up Reminder
        </a-button>
      </div>

      <!-- Payment History Table -->
      <div class="payment-history-section">
        <a-table
          :columns="paymentColumns"
          :data-source="paymentHistory"
          row-key="id"
          :loading="paymentsLoading"
          :pagination="paymentHistoryPagination"
          size="small"
          :scroll="{ x: 'max-content' }"
          style="background-color: #fafafa"
          @change="handlePaymentTableChange"
        >
          <template slot="amount" slot-scope="text">
            {{ formatAmount(text) }}
          </template>
          <template slot="date" slot-scope="text">
            {{ formatDate(text) }}
          </template>
          <template slot="status" slot-scope="text">
            <StatusTag :status="text" />
          </template>
          <template slot="notes" slot-scope="text">
            <div
              v-if="!text || text.trim() === ''"
              style="color: #999; font-style: italic"
            >
              -
            </div>
            <div v-else>
              <span v-if="text.length <= 50">{{ text }}</span>
              <div v-else>
                <span v-if="!expandedNotes[text]"
                  >{{ text.substring(0, 50) }}...</span
                >
                <span v-else>{{ text }}</span>
                <a
                  @click="toggleNoteExpansion(text)"
                  style="
                    color: #1890ff;
                    cursor: pointer;
                    margin-left: 4px;
                    font-size: 12px;
                  "
                >
                  {{ expandedNotes[text] ? "Show less" : "Show more" }}
                </a>
              </div>
            </div>
          </template>
          <template slot="attachments" slot-scope="text, record">
            <a-button
              v-if="record.attachments && record.attachments.length > 0"
              type="link"
              size="small"
              @click="viewPaymentProof(record.attachments[0])"
              style="color: #1890ff; padding: 0"
            >
              <i class="fa fa-eye" style="margin-right: 4px"></i>
              View Proof
            </a-button>
            <span v-else style="color: #999; font-size: 12px">No proof</span>
          </template>
        </a-table>
      </div>

      <!-- Keep Payment Proof functionality for future table use -->
      <div v-if="false" class="details-section">
        <h3 class="section-title">Payment Proof</h3>
        <div class="attachments-grid">
          <div
            v-for="(attachment, index) in repaymentData.attachments"
            :key="index"
            class="attachment-item"
            @click="viewAttachment(attachment)"
          >
            <div class="attachment-preview">
              <i
                class="fa fa-file-image"
                style="font-size: 24px; color: #1890ff"
              ></i>
            </div>
            <div class="attachment-info">
              <p class="attachment-name">Payment Proof {{ index + 1 }}</p>
              <p class="attachment-action">Click to view</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Document Preview Modal -->
    <DocumentPreviewModal
      :visible="previewVisible"
      :documentUrl="previewDocumentUrl"
      @close="handlePreviewCancel"
    />

    <!-- Create Repayment Modal -->
    <CreateRepaymentModal
      :visible="showCreateRepaymentModal"
      :preSelectedInvoice="repaymentData"
      @cancel="closeCreateRepaymentModal"
      @success="onRepaymentSuccess"
    />
  </a-drawer>
</template>

<script>
import StatusTag from "../shared-components/StatusTag.vue";
import DocumentPreviewModal from "../shared-components/DocumentPreviewModal.vue";
import CreateRepaymentModal from "./CreateRepaymentModal.vue";
import { formatDate, formatAmount, getFileDownloadUrl } from "../../utils";
import FinancingAndPaymentsService from "../../api/financing-and-payments.service";

export default {
  name: "RepaymentDetailsDrawer",
  components: {
    StatusTag,
    DocumentPreviewModal,
    CreateRepaymentModal,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    repaymentData: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      previewVisible: false,
      previewDocumentUrl: "",
      paymentHistory: [],
      paymentsLoading: false,
      expandedNotes: {}, // Track which notes are expanded
      showCreateRepaymentModal: false,
      sendingReminder: false,
      
      // Pagination for payment history
      paymentPagination: {
        pageIndex: 1,
        pageSize: 10,
        totalCount: 0,
      },
      paymentColumns: [
        {
          title: "Date",
          dataIndex: "repaymentDate",
          key: "date",
          scopedSlots: { customRender: "date" },
          width: 100,
        },
        {
          title: "Amount",
          dataIndex: "amount",
          key: "amount",
          scopedSlots: { customRender: "amount" },
          width: 100,
        },
        {
          title: "Method",
          dataIndex: "paymentMethod",
          key: "method",
          width: 120,
        },
        {
          title: "Recorded By",
          dataIndex: "collectionRoute",
          key: "recordedBy",
          width: 120,
        },
        {
          title: "Notes",
          dataIndex: "notes",
          key: "notes",
          width: 150,
          scopedSlots: { customRender: "notes" },
        },
        {
          title: "Status",
          dataIndex: "status",
          key: "status",
          scopedSlots: { customRender: "status" },
          width: 100,
        },
        {
          title: "Proof",
          key: "attachments",
          scopedSlots: { customRender: "attachments" },
          width: 80,
        },
      ],
    };
  },
  watch: {
    repaymentData: {
      handler(newData) {
        if (newData && newData.invoiceId) {
          this.fetchPaymentHistory();
        }
      },
      immediate: true,
    },
  },
  computed: {
    paymentHistoryPagination() {
      return {
        current: this.paymentPagination.pageIndex,
        pageSize: this.paymentPagination.pageSize,
        total: this.paymentPagination.totalCount,
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} of ${total} payments`,
        showSizeChanger: false,
      };
    },
  },
  methods: {
    formatDate,
    formatAmount,
    viewAttachment(attachment) {
      if (attachment) {
        this.previewDocumentUrl = getFileDownloadUrl(attachment);
        this.previewVisible = true;
      } else {
        this.$message.warning("Attachment not available");
      }
    },
    viewPaymentProof(attachment) {
      // Use the same method as viewAttachment for consistency
      this.viewAttachment(attachment);
    },
    handlePreviewCancel() {
      this.previewVisible = false;
      this.previewDocumentUrl = "";
    },
    recordNewRepayment() {
      // Open the modal directly in this component
      this.showCreateRepaymentModal = true;
    },
    closeCreateRepaymentModal() {
      this.showCreateRepaymentModal = false;
    },
    onRepaymentSuccess() {
      console.log("Repayment created successfully in drawer");
      this.showCreateRepaymentModal = false;
      this.fetchPaymentHistory(); // Refresh payment history
      this.$emit("repayment-success"); // Emit to parent to refresh main table
    },
    sendFollowUp() {
      const self = this;
      this.$confirm({
        title: "Send Payment Reminder",
        content: `Are you sure you want to send a payment reminder to ${this.repaymentData?.buyerName}?`,
        okText: "Send Reminder",
        okType: "primary",
        cancelText: "Cancel",
        onOk() {
          return self.sendPaymentReminder();
        },
      });
    },
    async sendPaymentReminder() {
      this.sendingReminder = true;
      const response = await FinancingAndPaymentsService.sendPaymentReminder(this.repaymentData.invoiceId);
      if (response.data?.code === 200) {
        this.$message.success("Payment reminder sent successfully!");
      } else {
        this.$message.error(
          response.data?.message ||
            "Failed to send payment reminder. Please try again."
        );
      }

      this.sendingReminder = false;
    },
    toggleNoteExpansion(noteText) {
      // Toggle the expansion state for this specific note
      this.$set(this.expandedNotes, noteText, !this.expandedNotes[noteText]);
    },
    refreshPaymentHistory() {
      // Public method to refresh payment history (called from parent)
      // Reset to first page when refreshing
      this.paymentPagination.pageIndex = 1;
      this.fetchPaymentHistory();
    },
    handlePaymentTableChange(pagination) {
      if (pagination.current !== this.paymentPagination.pageIndex) {
        this.paymentPagination.pageIndex = pagination.current;
        this.fetchPaymentHistory();
      }
    },
    async fetchPaymentHistory() {
      if (!this.repaymentData || !this.repaymentData.invoiceId) {
        return;
      }

      this.paymentsLoading = true;
      try {
        const params = {
          invoiceId: this.repaymentData.invoiceId,
          pageIndex: this.paymentPagination.pageIndex,
          pageSize: this.paymentPagination.pageSize,
        };

        const response = await FinancingAndPaymentsService.getRepayments(params);

        if (response.data?.code === 200) {
          const data = response.data.data;
          this.paymentHistory = data.results || [];
          this.paymentPagination.totalCount = data.totalCount || 0;
        } else {
          this.paymentHistory = [];
          this.paymentPagination.totalCount = 0;
          this.$message.warning("No payment history found for this invoice");
        }
      } catch (error) {
        console.error("Error fetching payment history:", error);
        this.paymentHistory = [];
        this.paymentPagination.totalCount = 0;
        this.$message.error("Failed to fetch payment history");
      } finally {
        this.paymentsLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Invoice Header */
.invoice-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.invoice-title {
  font-size: 16px;
  font-weight: 600;
  color: #262626;
}

.invoice-number {
  color: #262626;
}

.buyer-name {
  color: #666;
  font-weight: 400;
}

.status-badge {
  display: flex;
  align-items: center;
}

/* Section Styling */
.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 16px;
}

.summary-section {
  margin-bottom: 24px;
}

/* Summary Cards Grid */
.summary-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.summary-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #e9ecef;
}

.card-label {
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 8px;
  font-weight: 500;
}

.card-value {
  font-size: 16px;
  font-weight: 700;
}

.card-value.primary {
  color: #262626;
}

.card-value.secondary {
  color: #262626;
}

.card-value.success {
  color: #28a745;
}

.card-value.danger {
  color: #dc3545;
}

/* Action Buttons */
.action-buttons {
  margin-bottom: 24px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

/* Payment History Section */
.payment-history-section {
  margin-top: 24px;
}

.payment-history-section .ant-table {
  border: 1px solid #f0f0f0;
  border-radius: 6px;
}

.payment-history-section .ant-table-thead > tr > th {
  background: #fafafa;
  font-weight: 600;
  font-size: 12px;
  color: #262626;
  border-bottom: 1px solid #f0f0f0;
}

.payment-history-section .ant-table-tbody > tr > td {
  font-size: 12px;
  padding: 8px 16px;
}

.payment-history-section .ant-table-tbody > tr:hover > td {
  background: #f6ffed;
}

/* Legacy styles for attachment functionality (hidden but preserved) */
.details-section {
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid #f0f0f0;
}

.attachments-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
}

.attachment-item {
  display: flex;
  align-items: center;
  padding: 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  background: #fafafa;
}

.attachment-item:hover {
  border-color: #26a246;
  background: #f6ffed;
  transform: translateY(-1px);
}

.attachment-preview {
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #e6f7ff;
  border-radius: 6px;
}

.attachment-info {
  flex: 1;
}

.attachment-name {
  margin: 0 0 4px 0;
  font-weight: 500;
  color: #262626;
  font-size: 14px;
}

.attachment-action {
  margin: 0;
  font-size: 12px;
  color: #1890ff;
}
</style>
